import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useUiStore } from '../../features/settings/ui-store.js';
import * as api from '../services/integration-api.js';

const QUERY_KEY = ['connections'] as const;

/**
 * Query key for a workspace's connector status map. Keyed by the selected workspace id so the
 * cache is scoped per workspace and the query refetches when the switcher changes it. Invalidation
 * targets the `['connections']` prefix (see `useInvalidateConnections`), which matches every
 * workspace's entry.
 */
function connectionsKey(workspaceId: string | null) {
  return [...QUERY_KEY, workspaceId] as const;
}

/**
 * Reads a workspace's connector status map (jira / confluence / salesforce / testrail → connected
 * boolean). Drives the Connectors Directory card grid and the quality connector gates.
 *
 * Pass an explicit `workspaceId` to read a specific workspace (the manage tab targets the workspace
 * it manages, which may differ from the selected one). Omit it to follow the selected workspace from
 * the UI store, so switching workspaces refetches the correct scope. The query is keyed by the
 * effective id; invalidation targets the `['connections']` prefix and covers every workspace.
 */
export function useConnections(workspaceIdArg?: string | null) {
  const selected = useUiStore((state) => state.selectedWorkspaceId);
  const workspaceId = workspaceIdArg === undefined ? selected : workspaceIdArg;
  return useQuery({
    queryKey: connectionsKey(workspaceId),
    queryFn: () => api.getAuthStatus(workspaceId),
    staleTime: 30_000,
  });
}

/**
 * Reads a workspace's full connector presence for the surfaces that manage connections: per provider,
 * the list of connections the caller reaches (each with its `connection_id`, label and lane) plus the
 * flat connected/`resolved_from` fields, and beside the providers the deployment's
 * `externalTenantLaneAvailable`. The boolean {@link useConnections} drops all of it.
 *
 * Anything that acts on a connection — disconnect, reconnect, browse, register a knowledge source —
 * reads the list, never the flat fields: those describe whichever connection the server resolved first,
 * so a workspace reaching two SharePoints looks exactly like one reaching a single shared connection.
 *
 * Keyed under the same `['connections']` prefix as `useConnections`, so `useInvalidateConnections`
 * refreshes both after a connect/disconnect. Scope resolution matches `useConnections`: explicit
 * `workspaceId`, or the selected workspace when omitted.
 *
 * `enabled` is for a surface that is mounted long before it is looked at — the composer's attach dialog
 * lives in every chat page and asks for this only once it opens, rather than costing every page load a
 * request for a list nobody is reading yet.
 */
export function useConnectorPresence(
  workspaceIdArg?: string | null,
  enabled = true,
) {
  const selected = useUiStore((state) => state.selectedWorkspaceId);
  const workspaceId = workspaceIdArg === undefined ? selected : workspaceIdArg;
  return useQuery({
    queryKey: [...QUERY_KEY, workspaceId, 'presence'] as const,
    queryFn: () => api.getConnectorPresence(workspaceId),
    staleTime: 30_000,
    enabled,
  });
}

export function useInvalidateConnections() {
  const qc = useQueryClient();
  /**
   * Returns the invalidation's promise, so a caller that has to WAIT for the
   * refetched answer — the approval check on the SharePoint tenant choice — can.
   * Every other caller keeps firing and forgetting it.
   */
  return useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: QUERY_KEY }),
      qc.invalidateQueries({ queryKey: ['quality', 'connector-status'] }),
    ]);
  }, [qc]);
}

export const connectionsQueryKey = QUERY_KEY;
